import { type TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import { isType } from '@sorare/core/src/lib/gql';
import {
  desktopAndAbove,
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { CartPayment } from '@sorare/marketplace/src/components/cart/CartPayment';

import { CaptainTutorial } from './CaptainTutorial';
import { FirstLineupEdition } from './FirstLineupEdition';
import type {
  FirstLineup_currentUser,
  FirstLineup_so5Leaderboard,
} from './__generated__/index.graphql';

const CartPaymentWrapper = styled.div`
  padding: var(--double-unit);

  @media ${tabletAndAbove} {
    margin: auto;
    width: 70%;
  }
  @media ${laptopAndAbove} {
    margin: auto;
    width: 60%;
  }
  @media ${desktopAndAbove} {
    margin: auto;
    width: 50%;
  }
`;

enum Steps {
  FIRST_LINEUP_EDITION,
  PAYMENT,
  CAPTAIN_SELECTION,
}

type Props = {
  currentUser?: FirstLineup_currentUser | null;
  so5Leaderboard?: FirstLineup_so5Leaderboard;
};
export const FirstLineup = ({ currentUser, so5Leaderboard }: Props) => {
  const [step, setStep] = useState<Steps>(Steps.FIRST_LINEUP_EDITION);
  const cartCards =
    currentUser?.myCart?.mainCartItems
      .map(({ card }) => card)
      .filter(r => isType(r, 'Card')) || [];

  const [firstCardPictureUrl] = useState(cartCards.at(0)?.player.pictureUrl);

  const components = {
    [Steps.FIRST_LINEUP_EDITION]: (
      <FirstLineupEdition
        currentUser={currentUser}
        so5Leaderboard={so5Leaderboard}
        onSubmit={() => setStep(Steps.PAYMENT)}
      />
    ),
    [Steps.PAYMENT]: currentUser?.myCart && (
      <CartPaymentWrapper>
        <CartPayment
          myCart={currentUser.myCart}
          onSuccess={() => setStep(Steps.CAPTAIN_SELECTION)}
        />
      </CartPaymentWrapper>
    ),
    [Steps.CAPTAIN_SELECTION]: (
      <CaptainTutorial
        firstCardPictureUrl={firstCardPictureUrl}
        onSubmit={() => null}
      />
    ),
  };

  return components[step];
};

FirstLineup.fragments = {
  currentUser: gql`
    fragment FirstLineup_currentUser on CurrentUser {
      slug
      myCart(sport: $sport) {
        id
        mainCartItems {
          id
          card {
            slug
            ... on Card {
              slug
              player {
                slug
                pictureUrl
              }
            }
          }
        }
        ...CartPayment_cart
      }
      ...FirstLineupEdition_currentUser
    }
    ${FirstLineupEdition.fragments.currentUser}
    ${CartPayment.fragments.cart}
  ` as TypedDocumentNode<FirstLineup_currentUser>,
  so5Leaderboard: gql`
    fragment FirstLineup_so5Leaderboard on So5Leaderboard {
      slug
      ...FirstLineupEdition_so5Leaderboard
    }
    ${FirstLineupEdition.fragments.so5Leaderboard}
  ` as TypedDocumentNode<FirstLineup_so5Leaderboard>,
};
